import * as React from 'react';
import { StickyContainer, Sticky, StickyChildArgs } from 'react-sticky';
import * as cx from 'classnames';
import {
  withStyles,
  WithStyles,
  createStyles,
  Theme,
} from '@material-ui/core/styles';
import RootRef from '@material-ui/core/RootRef';

import { withIntl, IntlProps } from 'ha/i18n';

import SuggestionBar from '../containers/SuggestionBar';

import MediaQuery from 'ha/helpers/MediaQuery';
import units from 'ha/theme/units';

import Row from 'ha/ui/next/Row';
import Col from 'ha/ui/next/Col';
import Fader from 'ha/ui/next/Fader';
import Slide from '@material-ui/core/Slide';

import MobileFiltersModal from 'ha/components/Filters/MobileFiltersModal';
import FilterPopoverContext from 'ha/components/Filters/FilterPopoverContext';
import SearchMeta from 'ha/pages/Search/containers/SearchMeta';
import SearchMap from '../containers/SearchMap';
import SearchPageCrawl from '../containers/SearchPageCrawl';
import SearchResults from '../containers/SearchResults';
import SearchResultsLoader from '../containers/SearchResultsLoader';
import SearchOverview from '../containers/SearchOverview';
import MobileMap from '../containers/MobileMap';

import MobileFilters from '../containers/SearchFilters/MobileFilters';
import MobileSearchAlertModal from './SearchAlerts/MobileSearchAlertModal';

import { filterUpdateButtonText, DefaultTextType } from '../utils';

const IntercomStyle = () => (
  <style>
    {`
        .intercom-launcher-frame,
        .intercom-launcher-discovery-frame,
        .intercom-launcher-badge-frame {
          right: 3rem!important;
        }
      `}
  </style>
);

interface Props {
  location: {
    query: {
      page: string;
    };
  };
  isSuggestionBarVisible: boolean;
  previewNumberOfListings?: number;
}

interface State {
  layoutElement: HTMLDivElement | null;
}

const styles = (t: Theme) =>
  createStyles({
    wrapper: {
      display: 'flex',
      flexDirection: 'column',
      height: 'calc(100vh - 64px)',
      background: 'white',
    },
    limitHeight: {
      height: '100%',
    },
    results: {
      position: 'relative',
      borderRight: units.border(1, 'solid', t.palette.grey[300]),
      transition: 'padding-bottom 0.3s ease-out',
    },
    mapContainer: {
      position: 'relative',
    },
  });

export class SearchLayout extends React.Component<
  Props & IntlProps & WithStyles<typeof styles>,
  State
> {
  state = { layoutElement: null };

  private layout: React.RefObject<HTMLDivElement> = React.createRef();

  componentDidMount() {
    // eslint-disable-next-line react/no-did-mount-set-state
    this.setState({ layoutElement: this.layout.current });
  }

  updateButtonText() {
    const {
      intl: { T },
      previewNumberOfListings,
    } = this.props;

    return filterUpdateButtonText(
      T,
      DefaultTextType.ShowResults,
      previewNumberOfListings,
    );
  }

  render() {
    const {
      location: {
        query: { page },
      },
      isSuggestionBarVisible,
      classes,
      intl: { T },
    } = this.props;

    return (
      <StickyContainer>
        <MediaQuery>
          {({ lg, xl }) => (
            <div className={lg ? classes.wrapper : undefined}>
              <SearchMeta page={page} />
              <SearchPageCrawl page={page} />
              <IntercomStyle />

              <FilterPopoverContext.Provider value={this.state.layoutElement}>
                <RootRef rootRef={this.layout}>
                  <Row className={classes.limitHeight}>
                    <Col
                      xs={xl ? 7 : lg ? 6 : 12}
                      className={classes.limitHeight}
                    >
                      <Row
                        direction="column"
                        wrap="nowrap"
                        className={cx(classes.limitHeight, classes.results)}
                      >
                        <SearchResultsLoader />
                        <SearchOverview />
                        {!lg ? (
                          <React.Fragment>
                            <MobileFiltersModal
                              priceFilterTitle={T('Budget')}
                              filtersComponent={MobileFilters}
                              updateButtonText={this.updateButtonText()}
                            />
                            <MobileSearchAlertModal />
                          </React.Fragment>
                        ) : null}
                        <Fader>
                          <SearchResults />
                        </Fader>
                      </Row>
                    </Col>
                    {lg ? (
                      <Col xs={xl ? 5 : 6} className={classes.mapContainer}>
                        <SearchMap />
                      </Col>
                    ) : (
                      <MobileMap />
                    )}
                  </Row>
                </RootRef>
              </FilterPopoverContext.Provider>

              {isSuggestionBarVisible && (
                // additional offset for navbar in mobile layout to avoid hiding
                <Sticky topOffset={-200}>
                  {({ style }: StickyChildArgs) => (
                    <div style={{ ...style, bottom: 0, top: 'unset' }}>
                      <Slide direction="up" in={true}>
                        <SuggestionBar />
                      </Slide>
                    </div>
                  )}
                </Sticky>
              )}
            </div>
          )}
        </MediaQuery>
      </StickyContainer>
    );
  }
}

export default withIntl(withStyles(styles)(SearchLayout));
